import React from "react";
import { Tabs, Tab, Box, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";

const CustomTabs = ({
  tabs,
  value,
  onChange,
  position = "center",
  variant = "fullWidth",
  textColor = "primary",
  indicatorColor = "primary",
  tabSx = {},
  containerSx = {},
}) => {
  const isSmallScreen = useMediaQuery("(max-width: 600px)");
  const effectiveVariant = isSmallScreen ? "scrollable" : variant;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: position,
        ...containerSx,
      }}
    >
      <Tabs
        value={value}
        onChange={onChange}
        variant={effectiveVariant}
        textColor={textColor}
        indicatorColor={indicatorColor}
        sx={{
          minHeight: 48,
          borderRadius: "12px",
          backgroundColor: "background.paper",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          "& .MuiTabs-indicator": {
            height: "4px",
            borderRadius: "4px 4px 0 0",
            bottom: "auto",
            top: 0,
          },
          ...(effectiveVariant !== "fullWidth" && {
            width: "auto",
          }),
          ...containerSx,
          // Ensure smooth scrolling on small screens
          ...(isSmallScreen && {
            "& .MuiTabs-scroller": {
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            },
          }),
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            icon={tab.icon}
            iconPosition="start"
            label={tab.label}
            sx={{
              minHeight: 48,
              fontWeight: 600,
              fontSize: isSmallScreen ? "0.75rem" : "0.875rem",
              textTransform: "none",
              color: "text.secondary",
              minWidth: isSmallScreen ? "80px" : "auto",
              flex: isSmallScreen ? "0 0 auto" : undefined,
              "&.Mui-selected": {
                color: "primary.main",
              },
              "&:hover": {
                "& svg": {
                  transform: "scale(1.2)",
                },
              },
              ...tabSx,
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
};

CustomTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.node.isRequired,
    })
  ).isRequired,
  value: PropTypes.any.isRequired,
  onChange: PropTypes.func.isRequired,
  position: PropTypes.oneOf(["flex-start", "center", "flex-end"]),
  variant: PropTypes.oneOf(["fullWidth", "standard", "scrollable"]),
  textColor: PropTypes.oneOf(["primary", "secondary", "inherit"]),
  indicatorColor: PropTypes.oneOf(["primary", "secondary"]),
  tabSx: PropTypes.object,
  containerSx: PropTypes.object,
};

export default CustomTabs;
